import React, { useState, useMemo } from "react";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Link } from "react-router-dom";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

export const PERIOD_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "this_year", label: "This Year" },
  { value: "last_year", label: "Last Year" },
  { value: "custom", label: "Custom Range" },
];

export const GROUP_BY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
];

export const DEFAULT_ANALYTICS_FILTERS = {
  period: "this_month",
  groupBy: "monthly",
  startDate: "",
  endDate: "",
  sortOrder: "asc",
  search: "",
};

export const selectClass =
  "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#982598]/30 focus:border-[#982598] transition";

export const formatNumber = (value) => {
  if (value === null || value === undefined) return "--";
  const n = Number(value);
  if (!Number.isFinite(n)) return "--";
  return n.toLocaleString();
};

export const formatGrowth = (value) => {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const sign = n > 0 ? "+" : "";
  return `${sign}${n}%`;
};

export const formatCurrency = (value) => {
  if (value === null || value === undefined) return "--";
  const n = Number(value);
  if (!Number.isFinite(n)) return "--";
  return `Rs ${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

export const buildChartJsData = (chartPayload, color = "#982598") => {
  const labels = chartPayload?.labels?.length ? chartPayload.labels : ["No data"];
  const values = chartPayload?.datasets?.[0]?.data?.length
    ? chartPayload.datasets[0].data
    : [0];

  return {
    labels,
    datasets: [
      {
        label: chartPayload?.datasets?.[0]?.label || "Count",
        data: values,
        fill: true,
        borderColor: color,
        backgroundColor: `${color}20`,
        tension: 0.35,
        pointBackgroundColor: color,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2,
      },
    ],
  };
};

export const buildBarChartData = (items, labelKey, valueKey, color = "#982598") => {
  const safe = Array.isArray(items) ? items.slice(0, 12) : [];
  return {
    labels: safe.map((i) => i[labelKey] || i.key || "N/A"),
    datasets: [
      {
        label: "Count",
        data: safe.map((i) => Number(i[valueKey] ?? i.count) || 0),
        backgroundColor: `${color}cc`,
        borderRadius: 8,
        maxBarThickness: 42,
      },
    ],
  };
};

export const defaultChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "top" },
  },
  scales: {
    x: { grid: { display: false } },
    y: { beginAtZero: true, ticks: { precision: 0 } },
  },
};

export const MetricCard = ({
  title,
  value,
  subtitle,
  growth,
  accent = "#982598",
  isLoading,
}) => {
  const growthLabel = formatGrowth(growth);
  const growthPositive =
    growth !== null && growth !== undefined && Number(growth) >= 0;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 shadow-sm hover:shadow-md transition-shadow">
      <div
        className="absolute inset-x-0 top-0 h-1"
        style={{ background: accent }}
      />
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
        {title}
      </p>
      <div className="mt-2 text-3xl font-bold text-gray-900">
        {isLoading ? (
          <div className="h-9 flex items-center">
            <div
              className="w-6 h-6 border-2 border-gray-200 rounded-full animate-spin"
              style={{ borderTopColor: accent }}
            />
          </div>
        ) : (
          value
        )}
      </div>
      {(subtitle || growthLabel) && !isLoading && (
        <div className="mt-3 flex items-center justify-between gap-2 text-xs text-gray-500">
          <span className="line-clamp-2">{subtitle}</span>
          {growthLabel && (
            <span
              className={`shrink-0 rounded-full px-2 py-0.5 font-semibold ${
                growthPositive
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-red-50 text-red-600"
              }`}
            >
              {growthLabel}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export const ClickableMetricCard = ({ to, ...props }) => {
  if (!to) return <MetricCard {...props} />;
  return (
    <Link to={to} className="block group">
      <div className="group-hover:-translate-y-0.5 transition-transform duration-200">
        <MetricCard {...props} />
      </div>
      <p className="mt-1.5 text-[11px] font-medium text-[#982598] opacity-0 group-hover:opacity-100 transition-opacity px-1">
        View detailed analytics →
      </p>
    </Link>
  );
};

export const AnalyticsFilterBar = ({
  filters,
  onChange,
  onApply,
  onReset,
  extraFields = [],
  isLoading,
  error,
}) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm mb-6">
    <div className="flex items-center justify-between mb-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-800">Filters</h3>
        <p className="text-xs text-gray-400 mt-0.5">
          Refine analytics by period, grouping, and module fields
        </p>
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1.5">
          Period
        </label>
        <select
          className={selectClass}
          value={filters.period}
          onChange={(e) => onChange("period", e.target.value)}
        >
          {PERIOD_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1.5">
          Chart Grouping
        </label>
        <select
          className={selectClass}
          value={filters.groupBy}
          onChange={(e) => onChange("groupBy", e.target.value)}
        >
          {GROUP_BY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1.5">
          Sort Order
        </label>
        <select
          className={selectClass}
          value={filters.sortOrder || "asc"}
          onChange={(e) => onChange("sortOrder", e.target.value)}
        >
          <option value="asc">Ascending</option>
          <option value="desc">Descending</option>
        </select>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1.5">
          Search
        </label>
        <input
          type="text"
          className={selectClass}
          placeholder="Search..."
          value={filters.search || ""}
          onChange={(e) => onChange("search", e.target.value)}
        />
      </div>

      {filters.period === "custom" && (
        <>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              Start Date
            </label>
            <input
              type="date"
              className={selectClass}
              value={filters.startDate || ""}
              onChange={(e) => onChange("startDate", e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              End Date
            </label>
            <input
              type="date"
              className={selectClass}
              value={filters.endDate || ""}
              onChange={(e) => onChange("endDate", e.target.value)}
            />
          </div>
        </>
      )}

      {extraFields.map((field) => (
        <div key={field.key}>
          <label className="block text-xs font-medium text-gray-500 mb-1.5">
            {field.label}
          </label>
          {field.type === "select" ? (
            <select
              className={selectClass}
              value={filters[field.key] ?? ""}
              onChange={(e) => onChange(field.key, e.target.value)}
            >
              {(field.options || []).map((opt) => (
                <option key={String(opt.value)} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              type={field.type || "text"}
              className={selectClass}
              placeholder={field.placeholder || ""}
              value={filters[field.key] ?? ""}
              onChange={(e) => onChange(field.key, e.target.value)}
            />
          )}
        </div>
      ))}
    </div>

    {error && <p className="text-sm text-red-500 mt-3">{error}</p>}

    <div className="flex flex-wrap items-center gap-3 mt-5">
      <button
        type="button"
        onClick={onApply}
        disabled={isLoading}
        className="btn-primary px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60"
      >
        Apply Filters
      </button>
      <button
        type="button"
        onClick={onReset}
        disabled={isLoading}
        className="px-5 py-2.5 rounded-lg text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
      >
        Reset
      </button>
    </div>
  </div>
);

export const ComparisonCard = ({ title, comparison, isLoading, accent = "#982598" }) => {
  const growthLabel = formatGrowth(comparison?.growthPercentage);
  const growthPositive =
    comparison?.growthPercentage !== null &&
    comparison?.growthPercentage !== undefined &&
    Number(comparison.growthPercentage) >= 0;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
        {title}
      </p>
      {isLoading ? (
        <div className="h-16 flex items-center">
          <div
            className="w-6 h-6 border-2 border-gray-200 rounded-full animate-spin"
            style={{ borderTopColor: accent }}
          />
        </div>
      ) : (
        <>
          <div className="flex items-end justify-between gap-3">
            <div>
              <p className="text-xs text-gray-400">
                {comparison?.current?.label || "Current"}
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(comparison?.current?.total)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400">
                {comparison?.previous?.label || "Previous"}
              </p>
              <p className="text-lg font-semibold text-gray-500">
                {formatNumber(comparison?.previous?.total)}
              </p>
            </div>
          </div>
          {growthLabel && (
            <div>
              <p
                className={`text-xs font-semibold mt-3 ${
                  growthPositive ? "text-emerald-600" : "text-red-500"
                }`}
              >
                {growthLabel} vs previous period
              </p>
              <p className="text-[10px] text-gray-400 mt-1.5 leading-relaxed">
                * Previous period is the equivalent duration immediately preceding the selected period.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const ChartPanel = ({
  title,
  chartPayload,
  isLoading,
  color = "#982598",
  type = "line",
}) => {
  const [showTable, setShowTable] = useState(false);
  const data = buildChartJsData(chartPayload, color);
  const Chart = type === "bar" ? Bar : Line;

  const tableData = useMemo(() => {
    if (!chartPayload?.labels) return [];
    const labels = chartPayload.labels;
    const values = chartPayload.datasets?.[0]?.data || [];
    return labels.map((label, idx) => ({
      label,
      value: values[idx] ?? 0,
    }));
  }, [chartPayload]);

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <div>
          <h3 className="text-base font-semibold text-gray-800">{title}</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {chartPayload?.groupBy ? `Grouped ${chartPayload.groupBy}` : "Trend over time"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {chartPayload?.totals !== undefined && !isLoading && (
            <span className="text-xs text-gray-500">
              Total:{" "}
              <strong className="text-gray-800">
                {formatNumber(chartPayload.totals)}
              </strong>
            </span>
          )}
          {!isLoading && tableData.length > 0 && (
            <button
              onClick={() => setShowTable(!showTable)}
              className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
            >
              {showTable ? "View Chart" : "View Table"}
            </button>
          )}
        </div>
      </div>
      <div className="h-[320px] md:h-[400px]">
        {isLoading ? (
          <div className="h-full flex items-center justify-center">
            <div
              className="w-8 h-8 border-2 border-gray-200 rounded-full animate-spin"
              style={{ borderTopColor: color }}
            />
          </div>
        ) : showTable ? (
          <div className="overflow-y-auto h-full border border-gray-100 rounded-xl">
            <table className="w-full text-left text-xs text-gray-500">
              <thead className="text-[10px] text-gray-700 uppercase bg-gray-50 sticky top-0">
                <tr>
                  <th scope="col" className="px-4 py-2.5">Date / Period</th>
                  <th scope="col" className="px-4 py-2.5 text-right">Count</th>
                </tr>
              </thead>
              <tbody>
                {tableData.length === 0 ? (
                  <tr>
                    <td colSpan="2" className="px-4 py-3 text-center text-gray-400">
                      No data available
                    </td>
                  </tr>
                ) : (
                  tableData.map((row, idx) => (
                    <tr key={idx} className="bg-white border-b hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium text-gray-900">{row.label}</td>
                      <td className="px-4 py-2.5 text-right font-bold text-gray-700">{formatNumber(row.value)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <Chart data={data} options={defaultChartOptions} />
        )}
      </div>
    </div>
  );
};

export const BreakdownPanel = ({
  title,
  items,
  labelKey,
  valueKey = "count",
  isLoading,
  color = "#982598",
}) => {
  const chartData = buildBarChartData(items, labelKey, valueKey, color);

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
      <h3 className="text-base font-semibold text-gray-800 mb-4">{title}</h3>
      <div className="h-[280px]">
        {isLoading ? (
          <div className="h-full flex items-center justify-center">
            <div
              className="w-8 h-8 border-2 border-gray-200 rounded-full animate-spin"
              style={{ borderTopColor: color }}
            />
          </div>
        ) : !items?.length ? (
          <div className="h-full flex items-center justify-center text-sm text-gray-400">
            No breakdown data
          </div>
        ) : (
          <Bar data={chartData} options={defaultChartOptions} />
        )}
      </div>
    </div>
  );
};

export const PageHeader = ({ title, subtitle, backTo = "/dashboard" }) => (
  <div className="mb-6">
    <Link
      to={backTo}
      className="inline-flex items-center text-sm font-medium text-[#982598] hover:underline mb-2"
    >
      ← Back to Dashboard
    </Link>
    <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{title}</h1>
    {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
  </div>
);
